import { useEventBus } from '@/composables/useEventBus';
import { useFetch } from '@/composables/useFetch';
import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { findMockDishById, findMockRecipeByDishId, getMockDishForOnboarding, getMockRecipeForOnboarding } from '@/onboarding/mockData';
import { createActionStep, createInfoStep, createInputStep } from '@/onboarding/stepStyles';
import { useEnvStore } from '@/stores/envStore';
import type { Dish, Ingredient, RecipeStep, RecipeStepInstruction } from '@/types/dish';
import type { StepOptions } from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';

const t = (key: string, params: Record<string, unknown> = {}) => (i18n.global as unknown as Composer).t(key, params);
const eventBus = useEventBus();

export function getOrganizationSteps({ tour, router, waitForElement, dishesStore }: OnboardingStepDeps): StepOptions[] {
  return [
    // 13.3 - Transition explicative (bulle centrée)
    createInfoStep({
      id: 'transition-to-recipes-list',
      title: t('onboarding.chapters.organization.steps.transition-to-recipes-list.title'),
      text: t('onboarding.chapters.organization.steps.transition-to-recipes-list.text'),
      beforeShowPromise: async () => {
        await nextTick();
      },
      buttons: [
        {
          text: t('onboarding.chapters.organization.steps.transition-to-recipes-list.buttonLabel'),
          action: async () => {
            tour.value?.next();
          },
        },
      ],
    }),
    // 14.1 — Transition vers la liste des recettes
    createActionStep(
      {
        id: 'recipes-list',
        title: t('onboarding.chapters.organization.steps.recipes-list.title'),
        text: t('onboarding.chapters.organization.steps.recipes-list.text'),
        attachTo: { element: '.onboarding-my-recipes', on: 'top' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-my-recipes', event: 'click' },
        beforeShowPromise: async () => {
          // Si l'URL contient '/recipe-steps', on le retire de l'URL
          if (window.location.pathname.includes('/recipe-steps')) {
            const newPath = window.location.pathname.replace('/recipe-steps', '');
            await router.push(newPath + window.location.search + window.location.hash);
            await nextTick();
          }
        },
      },
      {
        placement: 'right',
      },
    ),

    // 14.2 — Mettre en avant le bouton 'Classeur'
    createActionStep(
      {
        id: 'show-folder-entry',
        title: t('onboarding.chapters.organization.steps.show-folder-entry.title'),
        text: t('onboarding.chapters.organization.steps.show-folder-entry.text', {
          dynamicText: `<strong>${t('onboarding.bold.chapter_5.folders')}</strong>`,
        }),
        attachTo: { element: '.onboarding-folder-highlight', on: 'bottom' },
        advanceOn: { selector: '.onboarding-folder-highlight', event: 'click' },
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path !== '/my-recipes') {
            console.log('[Onboarding] Retour à /my-recipes...');
            await router.push({ path: '/my-recipes' });
          }
          await waitForElement('.onboarding-folder-highlight', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),

    // 14.3 — Créer un classeur
    createActionStep(
      {
        id: 'add-folder',
        title: t('onboarding.chapters.organization.steps.add-folder.title'),
        text: t('onboarding.chapters.organization.steps.add-folder.text'),
        attachTo: { element: '.onboarding-add-folder', on: 'bottom' },
        advanceOn: { selector: '.onboarding-add-folder', event: 'click' },
        beforeShowPromise: async () => {
          // Fermer tout dialog ouvert
          const dialogSelector = '[data-pc-name="dialog"]';
          if (document.querySelector(dialogSelector)) {
            console.log('[Onboarding] Dialogue détecté. Attente de sa fermeture...');
            await new Promise<void>((resolve, reject) => {
              const timeout = 5000;
              const interval = 100;
              let elapsedTime = 0;
              const poller = setInterval(() => {
                elapsedTime += interval;
                const dialog = document.querySelector(dialogSelector);
                if (!dialog) {
                  clearInterval(poller);
                  resolve();
                  return;
                }
                if (elapsedTime >= timeout) {
                  clearInterval(poller);
                  reject(new Error('Timeout waiting for dialog to close'));
                }
              }, interval);
            });
          }
          await waitForElement('.onboarding-folders-section', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),

    // 14.4 — Remplir et valider le formulaire du dialog
    createInputStep({
      id: 'validate-folder-dialog',
      title: t('onboarding.chapters.organization.steps.validate-folder-dialog.title'),
      text: t('onboarding.chapters.organization.steps.validate-folder-dialog.text', {
        dynamicText: `<strong>${t('onboarding.bold.chapter_5.save')}</strong>`,
      }),
      attachTo: { element: '.custom-dish-tag-dialog', on: 'top' },
      advanceOn: { selector: '.custom-dish-tag-dialog-button-save', event: 'click' },
      beforeShowPromise: async () => {
        await waitForElement('.custom-dish-tag-dialog', 10000);
        await waitForElement('.custom-dish-tag-dialog-button-save', 10000);

        await nextTick();
      },
      buttons: [
        {
          text: t('onboarding.common.previous'),
          action: () => {
            console.log('[Onboarding] Tentative de fermeture du dialog via bouton Précédent');
            const selectors = [
              '[data-pc-name="dialog"] .p-dialog-header-close',
              '[data-pc-name="dialog"] .pi-times',
              '[data-pc-name="dialog"] button[aria-label="Close"]',
              '.p-dialog-header-close',
              '[custom-dish-tag-dialog-button-cancel]',
            ];
            let dialogClosed = false;
            for (const selector of selectors) {
              const element = document.querySelector(selector);
              if (element) {
                console.log(`[Onboarding] Trouvé sélecteur: ${selector}, tentative de clic`);
                (element as HTMLElement).click();
                dialogClosed = true;
                break;
              }
            }
            if (!dialogClosed) {
              console.log('[Onboarding] Aucun sélecteur de fermeture trouvé, émission événement direct');
              eventBus.emit('onboarding:dialog-canceled-by-user');
              return;
            }
            setTimeout(() => {
              if (tour.value) {
                console.log('[Onboarding] Navigation directe vers add-folder');
                tour.value.show('add-folder');
              }
            }, 200);
          },
          secondary: true,
        },
      ],
    }),

    // 14.5 — Sélectionner n'importe quel classeur
    createActionStep(
      {
        id: 'select-last-folder',
        title: t('onboarding.chapters.organization.steps.select-last-folder.title'),
        text: t('onboarding.chapters.organization.steps.select-last-folder.text'),
        attachTo: { element: '.onboarding-tags-list', on: 'top' },
        canClickTarget: true,
        advanceOn: {
          selector: '.onboarding-folder-item, .folder-card, [class*="folder"], [data-testid*="folder"]',
          event: 'click',
        },
        beforeShowPromise: async () => {
          // Si on est déjà dans un classeur, retourner à la liste
          if (router.currentRoute.value.path.startsWith('/my-recipes/tags/')) {
            console.log('[Onboarding] Retour à la liste des classeurs...');
            await router.push('/my-recipes');
            await nextTick();
          }

          // Attendre qu'au moins un classeur soit présent
          await waitForElement('.onboarding-folder-item, .folder-card, [class*="folder"]', 10000);
          await nextTick();
        },
      },
      {
        placement: 'top',
      },
    ),
    // 15.1 - Ajouter un sous-classeur
    createActionStep(
      {
        id: 'add-subfolder',
        title: t('onboarding.chapters.organization.steps.add-subfolder.title'),
        text: t('onboarding.chapters.organization.steps.add-subfolder.text'),
        attachTo: { element: '.onboarding-add-folder', on: 'bottom' },
        advanceOn: { selector: '.onboarding-add-folder', event: 'click' },
        beforeShowPromise: async () => {
          // Attendre que l'URL change vers une page de sous-classeur
          console.log('[Onboarding] 15.1: URL actuelle:', router.currentRoute.value.path);

          // Attendre que l'URL contienne '/my-recipes/tags/' (indiquant qu'on est dans un classeur)
          let attempts = 0;
          const maxAttempts = 50; // 5 secondes max
          while (!router.currentRoute.value.path.includes('/my-recipes/tags/') && attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            attempts++;
          }

          console.log('[Onboarding] 15.1: URL après attente:', router.currentRoute.value.path);

          await waitForElement('.onboarding-folders-section', 10000);
          await nextTick();
        },
      },
      {
        placement: 'top',
      },
    ),
    // 15.2 — Remplir et valider le formulaire du dialog
    createInputStep({
      id: 'validate-subfolder-dialog',
      title: t('onboarding.chapters.organization.steps.validate-subfolder-dialog.title'),
      text: t('onboarding.chapters.organization.steps.validate-subfolder-dialog.text', {
        dynamicText: `<strong>${t('onboarding.bold.chapter_5.save')}</strong>`,
      }),
      attachTo: { element: '[data-pc-name="dialog"]', on: 'top' },
      advanceOn: { selector: '#custom-dish-tag-dialog-button-save', event: 'click' },
      beforeShowPromise: async () => {
        await waitForElement('#custom-dish-tag-dialog-button-save', 10000);
        await nextTick();
      },
      buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
    }),
    // 16.1 — Retour à la liste des recettes
    createActionStep(
      {
        id: 'back-to-recipes-list',
        title: t('onboarding.chapters.organization.steps.back-to-recipes-list.title'),
        text: t('onboarding.chapters.organization.steps.back-to-recipes-list.text'),
        attachTo: { element: '.onboarding-my-recipes', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-my-recipes', event: 'click' },
        beforeShowPromise: async () => {
          const selector = '.onboarding-my-recipes';
          const element = document.querySelector(selector); // 1. Si l'élément est déjà là (ex: en reculant de 16.2), on n'attend pas.

          if (element) {
            console.log("[Onboarding] 16.1: Élément déjà visible, on n'attend pas l'événement.");
            return;
          }

          console.log('[Onboarding] 16.1: Attente de l\'événement "last-folder-added"...');
          await new Promise((resolve) => {
            const handler = () => {
              eventBus.off('onboarding:last-folder-added', handler);
              resolve(true);
            };
            eventBus.on('onboarding:last-folder-added', handler);
          });
          await waitForElement(selector, 10000);
          await nextTick();
        },
      },
      {
        placement: 'right',
      },
    ),
    // 16.1.5 — Ajouter une recette mock dans l'historique
    createInfoStep({
      id: 'add-mock-recipe',
      title: t('onboarding.chapters.organization.steps.add-mock-recipe.title'),
      text: t('onboarding.chapters.organization.steps.add-mock-recipe.text'),
      canClickTarget: false,
      beforeShowPromise: async () => {
        // Ajouter une recette mock dans le store
        const mockDish = getMockDishForOnboarding();

        // Vérifier si un plat mock a déjà été ajouté pour cette étape
        const existingMockId = localStorage.getItem('onboarding:generatedDishId');

        if (!existingMockId || existingMockId !== mockDish.id.toString()) {
          // S'assurer que le plat n'est pas déjà présent dans le store (éviter les doublons)
          const existingDishIndex = dishesStore.dishes.findIndex((dish) => dish.id === mockDish.id);
          if (existingDishIndex === -1) {
            dishesStore.dishes.unshift(mockDish);
            console.log('[Onboarding] Plat mock ajouté dans le store:', mockDish.name);
          }

          // Sauvegarder l'ID du plat mock dans localStorage avec la clé utilisée par GenerationHistoryView
          localStorage.setItem('onboarding:generatedDishId', mockDish.id.toString());
          console.log('[Onboarding] ID du plat mock sauvegardé dans localStorage:', mockDish.id);
        } else {
          console.log('[Onboarding] Plat mock déjà configuré, pas de duplication');
        }

        await nextTick();
      },
      buttons: [{ text: t('onboarding.common.next'), action: () => tour.value?.next() }],
    }),
    // 16.2 — Ouvrir l'historique des recettes
    createActionStep(
      {
        id: 'open-history',
        title: t('onboarding.chapters.organization.steps.open-history.title'),
        text: t('onboarding.chapters.organization.steps.open-history.text'),
        attachTo: { element: '.onboarding-history-highlight', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-history-highlight', event: 'click' },
        beforeShowPromise: async () => {
          if (router.currentRoute.value.path !== '/my-recipes') {
            console.log('[Onboarding] Retour à /my-recipes...');
            await router.push({ path: '/my-recipes' });
            await nextTick();
          }
          const historyList = document.querySelector('.onboarding-history-list');
          if (historyList) {
            console.log("[Onboarding] 16.2: Le panel d'historique est ouvert, fermeture...");
            const toggleButton = document.querySelector('.onboarding-history-highlight') as HTMLElement;
            if (toggleButton) {
              toggleButton.click();
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
          }
          await waitForElement('.onboarding-history-highlight', 10000);
          await nextTick();
        },
      },
      {
        placement: 'top',
      },
    ),
    // 16.3 — Classer une recette
    createActionStep(
      {
        id: 'classify-recipe',
        title: t('onboarding.chapters.organization.steps.classify-recipe.title'),
        text: t('onboarding.chapters.organization.steps.classify-recipe.text'),
        attachTo: { element: '.onboarding-history-list', on: 'bottom' },
        canClickTarget: true,
        advanceOn: { selector: '.onboarding-history-menu-button', event: 'click' },
        beforeShowPromise: async () => {
          // Attend qu'au moins une carte soit présente
          await waitForElement('.onboarding-history-list .onboarding-history-card', 10000);
          await nextTick();
        },
        buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
      },
      {
        placement: 'top',
      },
    ),
    // 16.4 - Ouvrir le dialog du choix de classeur
    createActionStep({
      id: 'menu-classify-dish',
      title: t('onboarding.chapters.organization.steps.menu-classify-dish.title'),
      text: t('onboarding.chapters.organization.steps.menu-classify-dish.text', {
        dynamicText: `<strong>${t('onboarding.bold.chapter_5.classify_dish')}</strong>`,
      }),
      attachTo: { element: '.onboarding-history-card-menu', on: 'top' },
      canClickTarget: true,
      beforeShowPromise: async () => {
        await waitForElement('.onboarding-history-card-menu', 10000);
        await nextTick();
      },
      buttons: [{ text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true }],
    }),

    // 16.5 — Choisir le classeur (Solution Modale)
    createActionStep(
      {
        id: 'choose-folder',
        title: t('onboarding.chapters.organization.steps.choose-folder.title'),
        text: t('onboarding.chapters.organization.steps.choose-folder.text'),
        attachTo: { element: '.onboarding-choose-folder-dialog', on: 'top' },
        canClickTarget: true,
        beforeShowPromise: async () => {
          await waitForElement('.onboarding-choose-folder-dialog', 10000);
          await nextTick();
        },

        advanceOn: { selector: '.onboarding-add-tag', event: 'click' },
      },
      {
        zIndex: '9998', // Z-index plus bas pour passer derrière le dropdown
      },
    ),
    // 16.6 — Confirmer la sauvegarde du plat
    createActionStep({
      id: 'confirm-save-dish',
      title: t('onboarding.chapters.organization.steps.confirm-save-dish.title'),
      text: t('onboarding.chapters.organization.steps.confirm-save-dish.text'),
      canClickTarget: false,
      buttons: [
        {
          text: t('onboarding.chapters.organization.steps.confirm-save-dish.buttonLabel.ignore'),
          secondary: true,
          action: () => {
            console.log("[Onboarding] Plat ignoré par l'utilisateur.");

            // Nettoyer les données d'onboarding
            localStorage.removeItem('onboarding:tags');
            dishesStore.selectedDish = null;
            console.log("[Onboarding] Tags locaux et plat sélectionné nettoyés suite à l'ignore");

            tour.value?.next();
          },
        },
        {
          text: t('onboarding.chapters.organization.steps.confirm-save-dish.buttonLabel.save'),
          action: async () => {
            console.log("[Onboarding] Plat sauvegardé par l'utilisateur.");
            const { fetchData } = useFetch();
            const envStore = useEnvStore();

            // Priorité 1: Utiliser le selectedDish du store s'il existe (contient les images générées/uploadées)
            let dishToSave: Dish | null = dishesStore.selectedDish;

            // Priorité 2: Si pas de selectedDish, utiliser la logique mock comme fallback
            if (!dishToSave) {
              const storedDishId =
                localStorage.getItem('onboarding_mock_dish_id') ||
                sessionStorage.getItem('onboarding_mock_dish_id') ||
                localStorage.getItem('onboarding:generatedDishId') ||
                sessionStorage.getItem('onboarding:generatedDishId');

              if (storedDishId) {
                const dishId = parseInt(storedDishId);
                if (dishId < 0) {
                  // Trouver le plat mock correspondant par son ID
                  dishToSave = dishesStore.dishes.find((dish) => dish.id === dishId) || findMockDishById(dishId);
                }
              }

              // Fallback vers le premier plat du store si pas trouvé
              if (!dishToSave) {
                dishToSave = dishesStore.dishes[0] || getMockDishForOnboarding();
              }
            }

            if (dishToSave) {
              // Récupérer la recette mock correspondante à ce plat spécifique
              const mockRecipe = findMockRecipeByDishId(dishToSave.id) || getMockRecipeForOnboarding();

              // Fonction pour convertir une URL d'image en File pour FormData
              // Remplacez votre fonction urlToFile par celle-ci plus robuste
              const urlToFile = async (url: string, filename: string): Promise<File | null> => {
                try {
                  const response = await fetch(url, { mode: 'cors' }); // 'cors' important si externe

                  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                  // VÉRIFICATION CRUCIALE : Est-ce bien une image ?
                  const contentType = response.headers.get('content-type');
                  if (contentType && contentType.includes('text/html')) {
                    console.warn(`[Onboarding] L'URL ${url} a renvoyé du HTML (probablement une 404 SPA fallback).`);
                    return null;
                  }

                  const blob = await response.blob();
                  return new File([blob], filename, { type: blob.type || 'image/webp' });
                } catch (error) {
                  console.warn('Erreur lors de la conversion URL vers File:', error);
                  return null;
                }
              };

              // Préparer les données de base
              const dishData = {
                dish_name: dishToSave.name,
                difficulty: dishToSave.difficulty,
                servings: dishToSave.servings,
                prep_time_min: dishToSave.prep_time_min,
                cook_time_min: dishToSave.cook_time_min,
                chill_time_min: dishToSave.chill_time_min || 0,
                calories_kcal: dishToSave.calories_kcal || null,
                protein_g: dishToSave.protein_g || null,
                fat_g: dishToSave.fat_g || null,
                saturated_fat_g: dishToSave.saturated_fat_g || null,
                carbohydrates_g: dishToSave.carbohydrates_g || null,
                sugars_g: dishToSave.sugars_g || null,
                dietary_fiber_g: dishToSave.dietary_fiber_g || null,
                salt_sodium_g: dishToSave.salt_sodium_g || null,
                allergens: dishToSave.allergens || null,
                selected_image: dishToSave.selected_image || 'none',
                ingredients:
                  dishToSave.ingredients?.map((ingredient: Ingredient) => ({
                    name: ingredient.name,
                    quantity: ingredient.quantity,
                    unit: ingredient.unit,
                    category: ingredient.category || null,
                    price: ingredient.price || null,
                  })) || [],
                recipe_steps:
                  mockRecipe.steps?.map((step: RecipeStep, stepIndex: number) => ({
                    name: step.name,
                    order: stepIndex + 1,
                    instructions:
                      step.instructions?.map((instruction: RecipeStepInstruction, instIndex: number) => ({
                        instruction: instruction.instruction,
                        order: instIndex + 1,
                      })) || [],
                  })) || [],
              };

              // Si le plat a des images, utiliser FormData avec les images
              const hasImages = dishToSave.image_url_gastro || dishToSave.image_url_bistro;

              if (hasImages) {
                console.log('Plat avec images détecté, envoi via FormData');
                const formData = new FormData();

                // Ajouter les données JSON
                formData.append('data', JSON.stringify(dishData));

                // Ajouter des images locales du dossier public pour l'onboarding
                if (dishToSave.image_url_gastro) {
                  // Utiliser l'image locale gastro du dossier public
                  const localImageUrl = dishToSave.image_url_gastro.startsWith('http')
                    ? dishToSave.image_url_gastro
                    : `${window.location.origin}${dishToSave.image_url_gastro}`;
                  const imageGastro = await urlToFile(localImageUrl, 'image_gastro.webp');
                  if (imageGastro) {
                    formData.append('image_gastro', imageGastro);
                  }
                }

                if (dishToSave.image_url_bistro) {
                  // Utiliser l'image locale bistro du dossier public
                  const localImageUrl = dishToSave.image_url_bistro.startsWith('http')
                    ? dishToSave.image_url_bistro
                    : `${window.location.origin}${dishToSave.image_url_bistro}`;
                  const imageBistro = await urlToFile(localImageUrl, 'image_bistro.webp');
                  if (imageBistro) {
                    formData.append('image_bistro', imageBistro);
                  }
                }

                try {
                  console.log('Onboarding FormData avec images', formData);
                  await fetchData(`${envStore.apiUrl}/dishes/onboarding/save`, 'POST', formData);
                  console.log('[Onboarding] Plat sauvegardé avec succès (avec images)');
                } catch (error) {
                  console.error('[Onboarding] Erreur lors de la sauvegarde:', error);
                }
              } else {
                // Pas d'images, envoi JSON classique
                console.log('Plat sans images, envoi via JSON');
                try {
                  console.log('Onboarding', dishData);
                  await fetchData(`${envStore.apiUrl}/dishes/onboarding/save`, 'POST', dishData);
                  console.log('[Onboarding] Plat sauvegardé avec succès (sans images)');
                } catch (error) {
                  console.error('[Onboarding] Erreur lors de la sauvegarde:', error);
                }
              }
            }

            tour.value?.next();
          },
        },
      ],
    }),
  ];
}
